import { ESTABLISHMENT_TYPE } from '../components/ProjectTypeSelect/utils';
import { FinancialSummaryInfo } from '../store/approval/types';

export enum ProjectStatus {
  NOT_STARTED = 'NOT_STARTED',
  DRAFT = 'DRAFT',
  RUNNING = 'RUNNING',
  QUALIFIED = 'QUALIFIED',
  UNQUALIFIED = 'UNQUALIFIED',
  RETURNED = 'RETURNED',
  CANCELLED = 'CANCELLED',
  ON_HOLD = 'ON_HOLD',
  DELIVERED = 'DELIVERED',
  CLOSED = 'CLOSED',
}

export enum TransactionType {
  BUY = 'BUY',
  NO_TRANSACTION = 'NO_TRANSACTION',
  LEASE = 'LEASE',
}

export enum ProjectRoles {
  PROJECT_ORDERER = 'projectOrderer',
  PROJECT_OWNER = 'projectOwner',
  PROJECT_ADMIN = 'projectAdmin',
  PROJECT_MANAGER = 'projectManager',
  CPH_PLANNING_PROJECT_LEADER = 'planningLeader',
  // CENTRES_PROJECT_MANAGER = 'centresProjectManager',
  // OFFICE_PROJECT_MANAGER = 'officeProjectManager',
  REAL_ESTATE_DEVELOPMENT_LEADER = 'reDevLead',
  CONSTRUCTION_PROJECT_MANAGER = 'constructionManager',
  DIGITAL_EXPANSION_LEADER = 'digitalLeader',
  // CUSTOMER_FULFILMENT_PROJECT_LEADER = 'customerFulfilmentProjectLeader',
  // COUNTRY_PROJECT_RESPONSIBLE 'countryProjectResponsible',
  // CLUSTER_REAL_ESTATE_MANAGER = 'clusterREManager',
  // REAL_ESTATE_CLUSTER_RESPONSIBLE = 'realEstateClusterResponsible',
  // DIGITAL_LEADER = 'digitalLeader',
}

export type InvestmentItem = {
  createdAt: Date;
  fiscalYear: string;
  id: string;
  landCost: number;
  landCostEur: null | number;
  projectId: string;
  propertyInvestment: null | number;
  propertyInvestmentEur: null | number;
  retailCost: null | number;
  retailCostEur: null | number;
  retailInvestment: null | number;
  retailInvestmentEur: null | number;
  totalInvestment: null | number;
  totalInvestmentEur: null | number;
  updatedAt: Date;
};
export type Responsible = {
  projectResponsibleRole: ProjectRoles;
  projectResponsiblePerson: string;
};

export type FiscalYearAlignedWithGEX = {
  projectId: string;
  fiscalYear: string;
  alignedWithGEXType: string;
  baselineStartDate: string;
  baselineOpeningDate: string;
};

export type ProjectResponsible = {
  projectId: string;
  projectResponsibleRole: string;
  projectResponsiblePerson: string;
  countries?: string[];
};

export type ExpansionProjectEditor = {
  name: string;
  email: string;
  expansionUserRole: string;
};

export type TableProject = {
  realEstateSISAN: boolean;
  id: string;
  projectSource?: string;
  businessUnitNumber: string;
  city: string;
  countryName: string;
  countryProjectResponsible: string;
  establishmentType: ESTABLISHMENT_TYPE;
  etrUnitId: string;
  projectCompletionDate: string;
  projectName: string;
  projectStartDate: Date;
  projectStatus: ProjectStatus;
  solutions?: ProjectSolutionType[];
  transactionType: TransactionType;
  unitType: string;
  projectResponsibleList?: ProjectResponsible[];
  expansionProjectEditors?: ExpansionProjectEditor[];
  isConfidential: boolean;
  isep: boolean;
  fiscalYearAlignedWithGEXList?: FiscalYearAlignedWithGEX[];
  partOfOMD: boolean;
  partOfCrp?: PartOfCrp[];
  countryCode?: string;
  referenceId?: string;
  unitCategory?: string;
  subPortfolio?: string;
  investments?: Partial<InvestmentItem>[];
  financialSummaries?: Array<
    {
      financialCategory:
        | 'SUBMITTED_FORECAST'
        | 'BASELINE'
        | 'RELEASED_APPROVED_BUDGET'
        | 'ACTUALS';
    } & Partial<InvestmentItem>
  >;
  financialInfo: {
    totalBudgetAllFY: number;
    totalBudget: number;
    totalLand: number;
    totalProperty: number;
    totalRetail: number;
    totalRetailCost: number;
  };
  financialSummaryInfo: {
    totalForecast: number;
    totalReleased: number;
    totalActuals: number;
  };
  financialSummaryInfos: FinancialSummaryInfo[];
  budgetSummary?: Record<string, number>;
  forecastSummary?: Record<string, number>;
  lastUpdatedDate?: string;
};

export type Project = TableProject & {
  // not sure about types because response was mostly null for values
  confidential: string;
  designStartDate: Date;
  projectStartDate: Date;
  partOfOnePlan?: boolean;
  projectStatusDate: Date;
  siteAccessibleDate: Date;
  isepDate?: Date;
  projectClosingDate: Date;
  targetProjectEndDate: Date;
  projectCompletionDate: Date;
  constructionStartDate: Date;
  internalAnnouncementDate: Date;
  externalAnnouncementDate: Date;
  handoverToLandlordDate: Date;
  finalDeedDate: Date;
  countryCode?: string;
  projectSource?: string;
  targetProjectStartDate: Date;
  siteAgreementSignedDate: Date;
  retailInvestment: number | null;
  propertyInvestment: number | null;
  costAtLeaseEnd: number | null;
  totalLeaseAmount: number | null;
  isLeasedFromAnotherIngkaCompany?: boolean;
  realEstateClusterResponsible?: string;
  solutionDescriptionAndCost?: string;
  siteLocationId?: string;
  state: string;
  address: string;
  unitType: string;
  siteType: string;
  landCost: string;
  landSize: string;
  unifierId: string;
  leasePeriod: string;
  projectName: string;
  locationName: string;
  siteProgress: string;
  projectStatus: string;
  programmeName: string;
  grossUnitArea: string;
  statusComments: string;
  transactionType: string;
  automationLevel: string;
  benefitsAndRisk: string;
  businessUnitType: string;
  grossBuildingArea: string;
  mergedWithReferenceId: string;
  totalProjectInvestment: string;
  stockedRangeAdjustment: string;
  needForAstroDecommissioning: string;
  etrProjectPrioritization: string;
  storageCapacityAdjustment: string;
  additionalProjectInformation: string;
  truckFulfilmentCapacityAdjustment: string;
  parcelFulfilmentCapacityAdjustment: string;
  etrSynced?: boolean;
  newUnitType?: string;
  reasonForClosing?: string;
  // Directive-related API fields
  projectReferenceId?: string;
  country?: string;
  initialProjectStartDate?: string;
  initialProjectDeliveryDate?: string;
  totalProjectAmountInclExternalLease?: number;
  retailCost?: number | null;
  solutionCategory?: string[];
  solutionPurpose?: string[];
  solutionType?: string[];
  projectBackgroundAndScope?: string;
  estimatedNetEffert?: string;
  alignedGex: {
    fy: string;
    value: string;
  }[];
  projectOrderer?: string;
  projectOwner?: string;
  projectManager?: string;
  projectAdmin?: string;
  clusterREManager?: string;
  reDevLead?: string;
  planningLeader?: string;
  constructionManager?: string;
  digitalLeader?: string;
  // remove below
  cbdName: string;
  businessUnitId: string;
  mtoolId: string;
  isep: boolean;
  unitCategory: string;
  buNumber: string;
  decisionPoints: DecisionPoint[];
  activityLog?: {
    content: any[];
  };
  msTeamsUrl: string;
  lastUpdatedDate: string;
  lastUpdatedUser: string;
  partOfCrp: PartOfCrp[];
  directive?: DirectiveResponse;
};

export type DirectiveResponse = {
  id?: string;
  projectName?: string;
  projectReferenceId?: string;
  country?: string;
  city?: string;
  unitCategory?: string;
  unitType?: string;
  establishmentType?: string;
  transactionType?: string;
  siteType?: string;
  solutionCategory?: string[];
  solutionPurpose?: string[];
  solutionType?: string[];
  dependenciesToOtherProjects?: string;
  initialProjectStartDate?: string;
  initialProjectDeliveryDate?: string;
  projectOwner?: string;
  totalProjectAmountInclExternalLease?: number;
  propertyInvestment?: number;
  retailInvestment?: number;
  retailCost?: number;
  externalLeaseCommitment?: number;
  propertyInvestmentEur?: number;
  retailInvestmentEur?: number;
  retailCostEur?: number;
  externalLeaseCommitmentEur?: number;
  totalProjectAmountInclExternalLeaseEur?: number;
  replenishmentUnit?: string;
  inboundCapacityThroughput?: number;
  truckFulfilmentCapacity?: number;
  parcelFulfilmentCapacity?: number;
  totalNumberOfFTEs?: number;
  totalNumberOfEmployees?: number;
};

export type PartOfCrp = {
  id: string;
  fiscalYear: string;
  approved: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
};

export enum ProjectPhaseOptions {
  FEASIBILITY = 'Feasibility',
  DESIGN_AND_DEVELOPMENT = 'Design & Development',
  IR_APPROVED = 'IR Approved',
  EXECUTION = 'Execution',
  MEASURE_AND_VERIFICATION = 'Measure & Verification',
}

export type ProjectSolutionType = {
  id: string;
  solutionDescription: string;
  solutionPurpose: string; // todo change to enum
  solutionType: string; // todo change to enum
  solutionCategory: string; // todo change to enum
};

export type ProjectSiteLocation = {
  id: string;
  countryCode: string;
  countryName: string;
  city: string;
  state: string;
  locationName: string;
  address: string;
};

export type DecisionPoint = {
  decisionPoint: string;
  decisionPointName: string;
  completedDate?: Date;
  status: string;
  active: boolean;
  requestId: string;
  overdraft?: boolean;
  pointsResult?: string;
};

export type Actuals = {
  amountEur: number;
  amount: number;
  fiscalYear: string;
};
